import type { AeEvaluatedQuestion } from '@/types/assessmentEngine';
import type { QuestionAnswerValue } from './QuestionRenderer';

export type PublicAnswerRow = {
  question_id: string;
  answer_text?: string | null;
  answer_numeric?: number | string | null;
  answer_json?: Record<string, unknown> | string | unknown[] | null;
  is_na?: boolean;
};

function parseJsonField(raw: PublicAnswerRow['answer_json']): unknown {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }
  return raw;
}

/** Map a persisted ae_response_answers row to QuestionRenderer value shape. */
export function publicAnswerRowToQuestionValue(
  row: PublicAnswerRow,
  question?: Pick<AeEvaluatedQuestion, 'question_type'> | null
): QuestionAnswerValue {
  if (row.is_na) return { is_na: true };

  const qType = question?.question_type;
  const json = parseJsonField(row.answer_json);

  if (qType === 'matrix' && json && typeof json === 'object' && !Array.isArray(json)) {
    return { answer_matrix: json as Record<string, number | 'NA'> };
  }

  if (qType === 'multi_choice' && Array.isArray(json)) {
    return { answer_values: json.map(String) };
  }

  if (Array.isArray(json) && json.length > 0) {
    return { answer_values: json.map(String) };
  }

  if (row.answer_numeric != null && row.answer_numeric !== '') {
    const n = Number(row.answer_numeric);
    if (!Number.isNaN(n)) return { answer_numeric: n, is_na: false };
  }

  if (row.answer_text != null && row.answer_text !== '') {
    return { answer_text: row.answer_text, is_na: false };
  }

  if (json && typeof json === 'object' && !Array.isArray(json)) {
    return { answer_matrix: json as Record<string, number | 'NA'> };
  }

  return {};
}

export function buildAnswersByQuestionId(
  rows: PublicAnswerRow[],
  questionsById: Map<string, AeEvaluatedQuestion>
): Record<string, QuestionAnswerValue> {
  const out: Record<string, QuestionAnswerValue> = {};
  for (const row of rows) {
    const q = questionsById.get(row.question_id);
    out[row.question_id] = publicAnswerRowToQuestionValue(row, q ?? null);
  }
  return out;
}
